import { useEffect, useState } from "react";
import { InventoryService } from "../product/Service";
import { CustomerService } from "../customer/Service";
import Select from "react-select";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { QuotationService } from "./Service";

const UpdateDeliveryChallan = () => {
  const { id } = useParams();
  // Services
  const service = new QuotationService();
  const inventoryService = new InventoryService();
  const customerService = new CustomerService();

  const [challanNumber, setChallanNumber] = useState("");
  const [customer, setCustomer] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [date, setDate] = useState("");
  const [discount, setDiscount] = useState(0);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<any[]>([]);
  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [roundOff, setRoundOff] = useState(false);

  // Options
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [gstOptions, setGstOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [productOptions, setProductOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);

  const loadDetails = async () => {
    const challan = await service.getDeliveryChallan(parseInt(id!));
    if (challan) {
      setChallanNumber(challan.challanNumber);
      setCustomer({
        label: challan.customer.name,
        value: challan.customer.id,
      });
      setDate(challan.date.split("T")[0].toString());
      setDiscount(challan.discount);
      setProducts(
        challan.products.map((p: any) => ({
          ...p.product,
          quantity: p.quantity,
        }))
      );
    }
  };

  useEffect(() => {
    loadDetails();
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // Product should not be empty
    if (products.length === 0) {
      toast.error("Please add products");
      return;
    }
    toast.loading("Updating delivery challan...");
    const data = {
      challanNumber: challanNumber,
      customer: customer?.value,
      date,
      discount,
      products: products.map((p: any) => ({
        product: p.id,
        quantity: p.quantity,
      })),
      roundOff,
    };
    const res = await service.updateDeliveryChallan(parseInt(id!), data);
    toast.dismiss();
    if (res) {
      toast.success("Delivery challan updated successfully");
    }
  };

  // Set options
  useEffect(() => {
    const fetchCustomers = async () => {
      const customers = await customerService.all();
      setCustomerOptions(
        customers
          .filter((c: any) => c.type === "Customer")
          .map((v: any) => ({
            label: v.name,
            value: v.id,
          }))
      );
    };
    const fetchProducts = async () => {
      const products = await inventoryService.allProducts();
      setProductOptions(
        products.map((v: any) => ({ label: v.name, value: v.id }))
      );
      setAllProducts(products.map((v: any) => ({ ...v, quantity: 1 })));
    };
    const gstOptions = [
      { label: "With GST", value: "true" },
      { label: "Without GST", value: "false" },
    ];
    setGstOptions(gstOptions);
    fetchCustomers();
    fetchProducts();
  }, []);

  useEffect(() => {
    // Calculate taxable amount
    const taxableAmount = products.reduce((acc: number, product: any) => {
      return acc + product.quantity * product.purchasePrice;
    }, 0);
    setTaxableAmount(taxableAmount);

    // Calculate discount
    const totalDiscount = discount > 0 ? (taxableAmount * discount) / 100 : 0;
    setTotalDiscount(totalDiscount);

    const total = taxableAmount - totalDiscount;
    setTotal(total);
  }, [products, roundOff, discount]);

  return (
    <>
      <div className="card mb-0">
        <div className="card-body">
          <div className="content-page-header">
            <h5>Add Delivery Challan</h5>
          </div>
          <form onSubmit={handleSubmit} className="row">
            <div className="col-md-12">
              <div className="form-group-item border-0 mb-0">
                <div className="row align-item-center">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Challan Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Challan Number"
                        value={challanNumber}
                        onChange={(e) => setChallanNumber(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Select Customer <span className="text-danger">*</span>
                      </label>
                      <ul className="form-group-plus css-equal-heights">
                        <li>
                          <Select
                            name="customer"
                            options={customerOptions}
                            value={customer}
                            onChange={setCustomer}
                            required={true}
                          />
                        </li>
                        <li>
                          <Link
                            className="btn btn-primary form-plus-btn"
                            to={"/customers/add"}
                          >
                            <i className="fas fa-plus-circle" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Challan Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="datetimepicker form-control"
                        placeholder="Select Date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="input-block mb-3">
                      <label>
                        Products <span className="text-danger">*</span>
                      </label>
                      <ul className="form-group-plus css-equal-heights">
                        <li>
                          <Select
                            name="product"
                            options={productOptions}
                            onChange={(e) => {
                              const product = allProducts.find(
                                (p: any) => p.id === e?.value
                              );
                              if (product) {
                                setProducts([...products, product]);
                              }
                            }}
                          />
                        </li>
                        <li>
                          <Link
                            className="btn btn-primary form-plus-btn"
                            to={"/products/add"}
                          >
                            <i className="fas fa-plus-circle" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group-item">
                <div className="card-table">
                  <div className="card-body">
                    <div className="table-responsive no-pagination">
                      <table className="table table-center table-hover datatable">
                        <thead className="thead-light">
                          <tr>
                            <th>Product / Service</th>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Rate</th>
                            <th>Discount</th>
                            <th>Tax</th>
                            <th>Amount</th>
                            <th className="no-sort">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product: any) => (
                            <tr key={product.id}>
                              <td>{product.name}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder="Enter quantity"
                                  value={product.quantity}
                                  onChange={(e) => {
                                    const newProducts = products.map(
                                      (p: any) => {
                                        if (p.id === product.id) {
                                          return {
                                            ...p,
                                            quantity: parseInt(e.target.value),
                                          };
                                        }
                                        return p;
                                      }
                                    );
                                    setProducts(newProducts);
                                  }}
                                />
                              </td>
                              <td>
                                {product.unit != null
                                  ? product.unit.symbol
                                  : ""}
                              </td>
                              <td>₹{product.purchasePrice}</td>
                              <td>0</td>
                              <td>0</td>
                              <td>
                                ₹{product.purchasePrice * product.quantity}
                              </td>
                              <td className="d-flex align-items-center h-100">
                                <button
                                  className="btn btn-danger btn-sm mb-2"
                                  onClick={() => {
                                    const newProducts = products.filter(
                                      (p: any) => p.id !== product.id
                                    );
                                    setProducts(newProducts);
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <div className="input-block mb-3">
                    <label>Discount(%)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={discount}
                      onChange={(e) => setDiscount(parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group-item border-0 p-0">
                <div className="row">
                  <div className="col-12">
                    <div className="form-group-bank">
                      <div className="invoice-total-box">
                        <div className="invoice-total-inner">
                          <p>
                            Taxable Amount <span>₹{taxableAmount}</span>
                          </p>
                          <p>
                            Discount <span>₹{totalDiscount}</span>
                          </p>
                          <div className="status-toggle justify-content-between">
                            <div className="d-flex align-center">
                              <p>Round Off </p>
                              <input
                                id="rating_1"
                                className="check"
                                type="checkbox"
                                defaultChecked={false}
                                onChange={(e) => {
                                  setRoundOff(e.target.checked);
                                }}
                              />
                              <label
                                htmlFor="rating_1"
                                className="checktoggle checkbox-bg"
                              >
                                checkbox
                              </label>
                            </div>
                            <span>₹{total}</span>
                          </div>
                        </div>
                        <div className="invoice-total-footer">
                          <h4>
                            Total Amount <span>₹{total}</span>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="btn-path text-end">
                <button
                  type="reset"
                  onClick={() => {
                    window.history.back();
                  }}
                  className="btn btn-cancel bg-primary-light me-2"
                >
                  Back
                </button>
                <button type="submit" className="btn btn-primary">
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateDeliveryChallan;
